import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';

export interface TableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string; // Example: '150px', '10%', etc.
  align?: 'left' | 'center' | 'right';
  format?: (value: any, row?: any) => string;
  /** Toon een kleine spinner i.p.v. celinhoud (bijv. async velden die nog null zijn) */
  cellPending?: (row: any) => boolean;
  actions?: Array<{
    label: string;
    icon?: string;
    hideLabel?: boolean;
    variant?: 'primary' | 'secondary' | 'danger';
    onClick: (row: any) => void;
  }>;
}

export interface TableSort {
  field: string;
  direction: 'asc' | 'desc';
}

@Component({
  selector: 'ui-table',
  standalone: true,
  imports: [CommonModule, IconComponent, ButtonComponent],
  template: `
    <div
      class="h-full flex flex-col rounded-lg relative border border-slate-200"
    >
      <!-- Loading Overlay -->
      <div
        *ngIf="loading"
        class="absolute inset-0 bg-white/70 z-10 flex items-center justify-center"
      >
        <ui-icon
          name="spinner"
          class="animate-spin text-primary-600"
          size="lg"
        />
      </div>

      <div class="flex-1 overflow-auto">
        <table class="w-full table-auto min-w-full">
          <colgroup>
            <col
              *ngFor="let col of columns"
              [style.width]="col.width"
              [style.minWidth]="col.width"
            />
          </colgroup>

          <!-- Fixed Header -->
          <thead class="sticky top-0 z-4 bg-slate-50 border-b border-slate-200">
            <tr [class.divide-x]="bordered" [class.divide-slate-200]="bordered">
              <th
                *ngFor="let col of columns"
                [class]="getHeaderClasses(col)"
                (click)="onSort(col)"
                class="rounded-t-lg"
              >
                {{ col.label }}
                <!-- <div [class]="getHeaderClasses(col)">

                 <ui-icon
                    *ngIf="col.sortable"
                    [name]="getSortIcon(col.key)"
                    size="sm"
                  />
                </div> -->
              </th>
            </tr>
          </thead>

          <!-- Scrollable Body -->
          <tbody class="bg-white divide-y divide-slate-200">
            <tr
              *ngFor="let row of data; let last = last"
              (click)="rowClick(row)"
              class="hover:bg-slate-50 cursor-pointer"
              [class.divide-x]="bordered"
              [class.divide-slate-200]="bordered"
            >
              <td
                *ngFor="let col of columns"
                [class]="getCellClasses(col)"
                [class.rounded-b-lg]="last && !(showPagination && meta)"
              >
                <ng-container *ngIf="!col.actions">
                  @if (col.cellPending?.(row)) {
                    <span
                      class="inline-flex items-center justify-center min-h-[1.25rem]"
                      aria-busy="true"
                      aria-label="Laden"
                    >
                      <ui-icon
                        name="spinner"
                        class="animate-spin text-primary-600"
                        size="sm"
                      />
                    </span>
                  } @else {
                    {{
                      col.format
                        ? col.format(getNestedValue(row, col.key), row)
                        : getNestedValue(row, col.key)
                    }}
                  }
                </ng-container>
                <div *ngIf="col.actions" class="flex justify-end">
                  <ui-button
                    *ngFor="let action of col.actions"
                    [variant]="action.variant || 'secondary'"
                    size="sm"
                    [icon]="action.icon"
                    [hideLabel]="action.hideLabel || true"
                    [iconOnly]="true"
                    (onClick)="onActionClick($event, row, action)"
                  >
                    {{ action.label }}
                  </ui-button>
                </div>
              </td>
            </tr>

            <!-- Empty State -->
            <tr *ngIf="!loading && (!data || data.length === 0)">
              <td
                [attr.colspan]="columns.length"
                class="px-6 py-8 text-center text-slate-500"
              >
                {{ emptyMessage }}
              </td>
            </tr>
          </tbody>

          <!-- Fixed Footer -->
          <tfoot
            *ngIf="showPagination && meta"
            class="sticky bottom-0 z-4 bg-slate-50 border-t border-slate-200"
          >
            <tr>
              <td [attr.colspan]="columns.length" class="rounded-b-lg">
                <div class="px-4 py-3 flex items-center justify-between">
                  <!-- Totals -->
                  <div class="text-sm text-slate-700">
                    <span>
                      {{ meta.from }} - {{ meta.to }}
                      <span *ngIf="meta.total"> van {{ meta.total }} resultaten</span>
                    </span>
                  </div>

                  <!-- Pagination Controls -->
                  <div class="flex items-center gap-2">
                    @if (meta.last_page && meta.last_page > 1) {
                      <ui-button
                        variant="secondary"
                        [disabled]="meta.current_page === 1"
                        (onClick)="onPageChange.emit(1)"
                        size="sm"
                        icon="chevrons-left"
                        [hideLabel]="true"
                      >
                        Eerste pagina
                      </ui-button>
                    }
                    <ui-button
                      variant="secondary"
                      [disabled]="meta.current_page === 1"
                      (onClick)="onPageChange.emit(meta.current_page - 1)"
                      size="sm"
                      icon="chevron-left"
                      [hideLabel]="true"
                    >
                      Vorige
                    </ui-button>

                    <span class="text-sm text-slate-700 px-2">
                      Pagina {{ meta.current_page }} <span *ngIf="meta.last_page">van {{ meta.last_page }}</span>
                    </span>

                    <ui-button
                      variant="secondary"
                      [disabled]="meta.current_page === meta.last_page || meta.is_last_page"
                      (onClick)="onPageChange.emit(meta.current_page + 1)"
                      size="sm"
                      icon="chevron-right"
                      [hideLabel]="true"
                    >
                      Volgende
                    </ui-button>

                    @if (meta.last_page && meta.last_page > 1) {
                    <ui-button
                      variant="secondary"
                      [disabled]="meta.current_page === meta.last_page"
                      (onClick)="onPageChange.emit(meta.last_page)"
                      size="sm"
                      icon="chevrons-right"
                      [hideLabel]="true"
                    >
                      Laatste pagina
                    </ui-button>
                    }
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  `,
})
export class TableComponent implements OnChanges {
  @Input() bordered: boolean = false;
  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() loading: boolean = false;
  @Input() emptyMessage: string = 'Geen resultaten gevonden';
  @Input() showPagination: boolean = true;
  @Input() meta?: {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
    from: number;
    to: number;
    is_last_page?: boolean;
  } | null = null;
  @Input() sort?: TableSort;

  @Output() onRowClick = new EventEmitter<any>();
  @Output() onPageChange = new EventEmitter<number>();
  @Output() onSortChange = new EventEmitter<TableSort>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
    }
  }

  getHeaderClasses(col: TableColumn): string {
    return `
      px-6
      py-3
      text-left
      text-xs
      font-medium
      text-slate-500
      uppercase
      tracking-wider
      ${col.sortable ? 'cursor-pointer hover:bg-slate-100' : ''}
      ${col.align === 'center' ? 'text-center' : ''}
      ${col.align === 'right' ? 'text-right' : ''}
    `;
  }

  getCellClasses(col: TableColumn): string {
    return `
      px-6
      py-4
      whitespace-nowrap
      text-sm
      text-slate-900
      ${col.align === 'center' ? 'text-center' : ''}
      ${col.align === 'right' ? 'text-right' : ''}
      border-slate-200
    `;
  }

  getSortIcon(key: string): string {
    if (!this.sort || this.sort.field !== key) return 'sort';
    return this.sort.direction === 'asc' ? 'sort-up' : 'sort-down';
  }

  onSort(col: TableColumn): void {
    if (!col.sortable) return;

    const direction =
      !this.sort || this.sort.field !== col.key
        ? 'asc'
        : this.sort.direction === 'asc'
        ? 'desc'
        : 'asc';

    this.onSortChange.emit({ field: col.key, direction });
  }

  rowClick(row: any) {
    this.onRowClick.emit(row);
  }

  onActionClick(event: Event, row: any, action: any): void {
    event.stopPropagation();
    action.onClick(row);
  }

  getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }
}
